import React from 'react';
import AdminPage from 'components/AdminPage';
import CompetitionToolbar from 'crud/competitions/CompetitionToolbar';
import CompetitionCard from 'crud/competitions/CompetitionCard';
import CompetitionForm from 'crud/competitions/CompetitionForm';
import { getYoutubeVideoId } from 'helpers';
import { object, string, boolean } from 'yup';

// Una fecha opcional que, si está, no puede ser anterior a `previous` (cuando
// esa también está). Se comparan como texto: "YYYY-MM-DD" ordena igual.
// `strict` pide que sea posterior: el paso de esta fecha empieza el día
// siguiente a `previous` (ver `getStepWindows` en `CompetitionContext`), así
// que con la misma fecha quedaría sin ningún día habilitado.
const optionalStage = (previous, message, strict = false) =>
	string().test(`after-${previous}`, message, function (value) {
		const previousValue = this.parent[previous];

		if (!value || !previousValue) {
			return true;
		}

		return strict ? value > previousValue : value >= previousValue;
	});

export default function Competitions() {
	const schema = object({
		actived: boolean(),
		title: string().required('El título es obligatorio'),
		entryStage: string().required('La apertura de postulaciones es obligatoria'),
		selectionStage: string()
			.required('El cierre de postulaciones es obligatorio')
			.test(
				'selection-after-entry',
				'El cierre de postulaciones no puede ser antes de la apertura',
				function (value) {
					const { entryStage } = this.parent;

					return !value || !entryStage || value >= entryStage;
				}
			),
		judgingStage: string()
			.required('La entrega de premios es obligatoria')
			.test(
				'judging-after-selection',
				'La entrega de premios no puede ser antes del cierre de postulaciones',
				function (value) {
					const { selectionStage } = this.parent;

					return !value || !selectionStage || value >= selectionStage;
				}
			),
		// Fechas internas del proceso: opcionales, pero si están tienen que ir
		// en orden entre el cierre de postulaciones y la entrega de premios.
		// Cada una cierra un paso del jurado: la preselección y la elección de
		// finalistas sólo se pueden hacer dentro de esas fechas.
		curationStage: optionalStage('selectionStage', 'El comité de postulaciones tiene que ser después del cierre de postulaciones', true),
		finalistsStage: optionalStage('curationStage', 'La comunicación a finalistas tiene que ser después del comité de postulaciones', true),
		votingStartStage: optionalStage('finalistsStage', 'La votación no puede empezar antes de la comunicación a finalistas'),
		votingEndStage: optionalStage('votingStartStage', 'El cierre de la votación no puede ser antes de su inicio').test(
			'voting-before-awards',
			'La votación tiene que cerrar antes de la entrega de premios',
			function (value) {
				const { judgingStage } = this.parent;

				// Estricto: los ganadores se eligen entre el día siguiente al
				// cierre de la votación y la entrega de premios.
				return !value || !judgingStage || value < judgingStage;
			}
		),
		// Opcional: si está vacío se usa el video por defecto de la home.
		videoUrl: string().test(
			'is-youtube-url',
			'El link tiene que ser de un video de YouTube',
			(value) => !value || Boolean(getYoutubeVideoId(value))
		)
	});

	const sortOptions = [
		{
			value: 'title',
			label: 'Título'
		}
	];

	return (
		<AdminPage
			title="Competiciones"
			seoTitle="Administración de la competición"
			sortOptions={sortOptions}
			collection="competitions"
			schema={schema}
			ItemFormFields={CompetitionForm}
			ItemCard={CompetitionCard}
			ItemToolBar={CompetitionToolbar}
		/>
	);
}
